import { useCallback, useEffect, useRef } from 'react';

import { useMagicIslandController } from './MagicIslandContext';
import type { IslandMode } from './types';

interface IslandSectionOptions {
    /** Higher wins when two sections clear the threshold at once. Default 0. */
    priority?: number;
    /** Skip registration entirely (e.g. suppress a mode on mobile). Default true. */
    enabled?: boolean;
}

/**
 * Registers a section's desired island content with the controller and returns a
 * callback ref to spread onto the section's root element. The controller measures
 * that element on each scroll pass to decide which section is active (≥ 60% in
 * view).
 *
 * - A no-op when there is no provider (feature off) or `enabled` is false, so
 *   sections can call it unconditionally.
 * - `mode` must be referentially stable across renders (wrap it in `useMemo`),
 *   otherwise the live-update effect fires every render.
 * - The ref is a callback ref so the registry's element stays in sync as the
 *   section mounts / unmounts — e.g. a branch that only renders the ref'd element
 *   on mobile, or content that mounts after registration. It never holds a stale
 *   null element.
 */
export const useIslandSection = <T extends HTMLElement = HTMLElement>(
    mode: IslandMode,
    options?: IslandSectionOptions,
) => {
    const controller = useMagicIslandController();
    const elRef = useRef<T | null>(null);
    const idRef = useRef<symbol | null>(null);
    const id = (idRef.current ??= Symbol('island-section'));
    const priority = options?.priority ?? 0;
    const enabled = options?.enabled !== false;

    // Latest mode, read by the register effect without re-running it.
    const modeRef = useRef(mode);
    modeRef.current = mode;

    // Register / unregister on mount (and when enabled / priority changes).
    useEffect(() => {
        if (!controller || !enabled) return;
        controller.register({
            id,
            priority,
            mode: modeRef.current,
            el: elRef.current,
        });
        return () => controller.unregister(id);
    }, [controller, enabled, priority, id]);

    // Push live mode updates (label / active index / play state) without
    // re-registering.
    useEffect(() => {
        if (!controller || !enabled) return;
        controller.update(id, { mode });
    }, [controller, enabled, id, mode]);

    // Callback ref — keep the registry's measured element current as it attaches
    // or detaches (update is a no-op before registration / when disabled).
    return useCallback(
        (node: T | null) => {
            elRef.current = node;
            if (controller && enabled) controller.update(id, { el: node });
        },
        [controller, enabled, id],
    );
};
